import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, PlusIcon, SendIcon, ShieldCheckIcon, FileTextIcon, CalculatorIcon, AlertCircleIcon, GraduationCapIcon, TrendingUpIcon, CreditCardIcon, BellIcon, ChevronRightIcon } from 'lucide-react';
import { formatNaira } from '../utils/format';
import { TopBar } from '../components/TopBar';
interface DashboardProps {
  navigate: (screen: string) => void;
}
export function Dashboard({
  navigate
}: DashboardProps) {
  const walletBalance = 2450000;
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 24
      }
    }
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="flex-1 overflow-y-auto pb-24 bg-slate-50" data-id="element-407">
      <TopBar onNotificationClick={() => navigate('notifications')} data-id="element-408" />

      <div className="px-5 pt-2 pb-6" data-id="element-409">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} className="mb-6" data-id="element-410">
          <h1 className="text-2xl font-bold text-[#1B3B36] flex items-center gap-2" data-id="element-411">
            Good morning, Adaeze
            <motion.span animate={{
            rotate: [0, 14, -8, 14, -4, 10, 0, 0]
          }} transition={{
            duration: 2.5,
            repeat: Infinity,
            repeatDelay: 3
          }} className="inline-block origin-bottom-right" data-id="element-412">
              👋
            </motion.span>
          </h1>
          <p className="text-sm text-gray-500 font-medium" data-id="element-413">
            Your protection score is looking good!
          </p>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" animate="show" className="space-y-6" data-id="element-414">
          {/* Main Wallet Card */}
          <motion.div variants={itemVariants} className="relative perspective-1000" data-id="element-415">
            <div className="relative rounded-3xl p-6 text-white shadow-2xl bg-gradient-to-br from-[#0F2B27] via-[#1B3B36] to-[#2A5A4A] overflow-hidden" data-id="element-416">
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full blur-3xl -mr-10 -mt-10" data-id="element-417"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-amber-500/10 rounded-full blur-2xl -ml-10 -mb-10" data-id="element-418"></div>

              <div className="relative z-10" data-id="element-419">
                <div className="flex justify-between items-start mb-8" data-id="element-420">
                  <div data-id="element-421">
                    <p className="text-white/70 text-xs font-bold uppercase tracking-widest mb-1" data-id="element-422">
                      Total Balance
                    </p>
                    <div className="inline-flex items-center gap-1 bg-white/10 backdrop-blur-md px-2 py-1 rounded-md text-[10px] font-medium" data-id="element-423">
                      <ShieldCheckIcon className="w-3 h-3 text-emerald-400" data-id="element-424" />{' '}
                      Fully Protected
                    </div>
                  </div>
                  <div className="w-10 h-10 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center border border-white/20" data-id="element-425">
                    <CreditCardIcon className="w-5 h-5 text-white" data-id="element-426" />
                  </div>
                </div>

                <motion.h2 initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                duration: 0.5,
                delay: 0.2
              }} className="text-4xl font-bold mb-6 tracking-tight" data-id="element-427">
                  {formatNaira(walletBalance)}
                </motion.h2>

                <div className="flex gap-3" data-id="element-428">
                  <button onClick={() => navigate('fund-wallet')} className="flex-1 bg-amber-500 hover:bg-amber-400 text-[#1B3B36] font-bold py-3.5 px-4 rounded-xl flex items-center justify-center gap-2 transition-all active:scale-95 shadow-lg shadow-amber-500/20" data-id="element-429">
                    <PlusIcon className="w-4 h-4" strokeWidth={3} data-id="element-430" /> Fund
                  </button>
                  <button onClick={() => navigate('transfer')} className="flex-1 bg-white/10 hover:bg-white/20 border border-white/10 text-white font-bold py-3.5 px-4 rounded-xl flex items-center justify-center gap-2 backdrop-blur-md transition-all active:scale-95" data-id="element-431">
                    <SendIcon className="w-4 h-4" data-id="element-432" /> Transfer
                  </button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Quick Actions Grid */}
          <motion.div variants={itemVariants} data-id="element-433">
            <h3 className="text-lg font-bold text-[#1a1a2e] mb-4" data-id="element-434">
              Quick Actions
            </h3>
            <div className="grid grid-cols-2 gap-3" data-id="element-435">
              {[{
              icon: ShieldCheckIcon,
              label: 'Get Covered',
              desc: 'Browse products',
              color: 'from-blue-50 to-blue-100/50 text-blue-600',
              action: () => navigate('products')
            }, {
              icon: CalculatorIcon,
              label: 'Calculator',
              desc: 'Plan savings',
              color: 'from-emerald-50 to-emerald-100/50 text-emerald-600',
              action: () => navigate('product-detail')
            }, {
              icon: FileTextIcon,
              label: 'Documents',
              desc: 'View certs',
              color: 'from-purple-50 to-purple-100/50 text-purple-600',
              action: () => navigate('policies')
            }, {
              icon: AlertCircleIcon,
              label: 'Make Claim',
              desc: 'Report incident',
              color: 'from-rose-50 to-rose-100/50 text-rose-600',
              action: () => navigate('claims')
            }].map((item, idx) => <button key={idx} onClick={item.action} className={`bg-gradient-to-br ${item.color.split(' ')[0]} ${item.color.split(' ')[1]} border border-white p-4 rounded-2xl flex flex-col items-start gap-3 shadow-sm hover:shadow-md transition-all active:scale-95`} data-id="element-436">
                  <div className={`w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center ${item.color.split(' ')[2]}`} data-id="element-437">
                    <item.icon className="w-5 h-5" data-id="element-438" />
                  </div>
                  <div className="text-left" data-id="element-439">
                    <span className="block text-sm font-bold text-[#1a1a2e]" data-id="element-440">
                      {item.label}
                    </span>
                    <span className="block text-[10px] font-medium text-gray-500 mt-0.5" data-id="element-441">
                      {item.desc}
                    </span>
                  </div>
                </button>)}
            </div>
          </motion.div>

          {/* Active Policies Summary */}
          <motion.div variants={itemVariants} data-id="element-442">
            <div className="flex justify-between items-center mb-4 px-1" data-id="element-443">
              <h2 className="text-lg font-bold text-[#1B3B36]" data-id="element-444">
                Active Policies
              </h2>
              <button onClick={() => navigate('policies')} className="text-xs font-bold text-gray-500 hover:text-[#1B3B36] transition-colors flex items-center" data-id="element-445">
                View All <ChevronRightIcon className="w-4 h-4" data-id="element-446" />
              </button>
            </div>

            <div className="space-y-3" data-id="element-447">
              <div onClick={() => navigate('policies')} className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm flex items-center justify-between cursor-pointer hover:border-gray-200 transition-colors" data-id="element-448">
                <div className="flex items-center gap-4" data-id="element-449">
                  <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center text-blue-600" data-id="element-450">
                    <GraduationCapIcon className="w-6 h-6" data-id="element-451" />
                  </div>
                  <div data-id="element-452">
                    <h3 className="font-bold text-[#1B3B36] text-sm" data-id="element-453">
                      Child Education
                    </h3>
                    <p className="text-xs text-gray-500" data-id="element-454">Leadway Assurance</p>
                  </div>
                </div>
                <div className="text-right" data-id="element-455">
                  <span className="block text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md mb-1" data-id="element-456">
                    Active
                  </span>
                  <span className="text-[10px] font-bold text-gray-400" data-id="element-457">
                    ₦16,666/mo
                  </span>
                </div>
              </div>

              <div onClick={() => navigate('policies')} className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm flex items-center justify-between cursor-pointer hover:border-gray-200 transition-colors" data-id="element-458">
                <div className="flex items-center gap-4" data-id="element-459">
                  <div className="w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center text-purple-600" data-id="element-460">
                    <TrendingUpIcon className="w-6 h-6" data-id="element-461" />
                  </div>
                  <div data-id="element-462">
                    <h3 className="font-bold text-[#1B3B36] text-sm" data-id="element-463">
                      General Annuity
                    </h3>
                    <p className="text-xs text-gray-500" data-id="element-464">Stanbic IBTC</p>
                  </div>
                </div>
                <div className="text-right" data-id="element-465">
                  <span className="block text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md mb-1" data-id="element-466">
                    Active
                  </span>
                  <span className="text-[10px] font-bold text-gray-400" data-id="element-467">
                    ₦50,000/mo
                  </span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Educational Banner */}
          <motion.div variants={itemVariants} className="bg-gray-900 rounded-3xl p-5 text-white flex items-center justify-between shadow-lg" data-id="element-468">
            <div data-id="element-469">
              <h3 className="font-bold text-sm mb-1" data-id="element-470">Insurance 101</h3>
              <p className="text-xs text-gray-400" data-id="element-471">
                Learn how claims work in 2 mins
              </p>
            </div>
            <button className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center backdrop-blur-sm hover:bg-white/20 transition-colors" data-id="element-472">
              <ArrowRightIcon className="w-5 h-5" data-id="element-473" />
            </button>
          </motion.div>
        </motion.div>
      </div>
    </motion.div>;
}